// =============================================================================
// IconCombobox - Selettore icona con ricerca testuale
// =============================================================================
// Dropdown ricercabile (digita per filtrare, frecce + Invio, click) speculare a
// ReasonCombobox: occupa una riga sola invece di una griglia di pulsanti.
// La prima opzione (`value` vuoto) è "nessuna icona": in quel caso l'entità
// mostra l'iniziale del proprio nome al posto dell'icona.

import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Search, ChevronDown, Check, Type as TypeIcon, type LucideIcon } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface IconOption {
  /** Nome icona lucide; stringa vuota = nessuna icona. */
  name: string;
  /** Etichetta tradotta mostrata nella lista e usata per la ricerca. */
  label: string;
  Icon: LucideIcon | null;
}

interface IconComboboxProps {
  options: IconOption[];
  value: string;
  onChange: (name: string) => void;
  /** Colore con cui rendere le icone (quello dell'entità in modifica). */
  color?: string;
  placeholder?: string;
  noMatchLabel?: string;
  className?: string;
}

export function IconCombobox({
  options,
  value,
  onChange,
  color,
  placeholder,
  noMatchLabel = '—',
  className,
}: IconComboboxProps) {
  const listboxId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = useMemo(
    () => options.find((o) => o.name === value) ?? options[0],
    [options, value],
  );

  const [query, setQuery] = useState(selected?.label ?? '');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Mantieni l'input allineato alla selezione quando cambia dall'esterno.
  useEffect(() => {
    setQuery(selected?.label ?? '');
  }, [selected]);

  // Quando l'input mostra esattamente l'etichetta selezionata non stiamo filtrando.
  const isFiltering = query.trim() !== '' && query !== selected?.label;
  const filtered = useMemo(() => {
    if (!isFiltering) return options;
    const q = query.trim().toLowerCase();
    // Match su etichetta tradotta e sul nome lucide (chi lo conosce lo cerca).
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.name.toLowerCase().includes(q),
    );
  }, [options, query, isFiltering]);

  // Reset evidenziazione all'apertura o al cambio dei risultati.
  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  // Chiudi cliccando fuori.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        closeAndSync();
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected]);

  const closeAndSync = () => {
    setOpen(false);
    setQuery(selected?.label ?? '');
  };

  const selectOption = (o: IconOption) => {
    onChange(o.name);
    setQuery(o.label);
    setOpen(false);
    inputRef.current?.blur();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) setOpen(true);
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      if (open && filtered.length > 0) {
        e.preventDefault();
        selectOption(filtered[Math.min(highlight, filtered.length - 1)]);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeAndSync();
    }
  };

  const SelectedIcon = selected?.Icon ?? TypeIcon;

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        {isFiltering ? (
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        ) : (
          <SelectedIcon
            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
            style={{ color: selected?.Icon ? color : undefined }}
          />
        )}
        <input
          ref={inputRef}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          // Seleziona il testo al focus: si digita subito la ricerca senza
          // dover cancellare a mano l'etichetta dell'icona corrente.
          onFocus={(e) => {
            setOpen(true);
            e.target.select();
          }}
          onKeyDown={handleKeyDown}
          className="w-full pl-10 pr-9 py-2 input"
        />
        <ChevronDown
          className={cn(
            'absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform',
            open && 'rotate-180',
          )}
        />
      </div>

      {open && (
        <ul
          id={listboxId}
          role="listbox"
          className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1"
        >
          {filtered.length === 0 ? (
            <li className="px-3 py-2 text-sm text-gray-400">{noMatchLabel}</li>
          ) : (
            filtered.map((o, idx) => {
              const Icon = o.Icon ?? TypeIcon;
              return (
                <li
                  key={o.name || '__none__'}
                  role="option"
                  aria-selected={o.name === value}
                  onMouseEnter={() => setHighlight(idx)}
                  // mousedown invece di click: previene il blur dell'input prima della selezione
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectOption(o);
                  }}
                  className={cn(
                    'flex items-center gap-2 px-3 py-2 text-sm cursor-pointer',
                    idx === highlight ? 'bg-emerald-50' : 'hover:bg-gray-50',
                  )}
                >
                  <Icon
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: o.Icon ? color : undefined }}
                  />
                  <span className="flex-1 min-w-0 text-gray-900 truncate">{o.label}</span>
                  {o.name === value && <Check className="w-4 h-4 text-emerald-600 flex-shrink-0" />}
                </li>
              );
            })
          )}
        </ul>
      )}
    </div>
  );
}
